(ns manage.product-selector-page.hooks
  (:require [cljs-bean.core :refer [->clj ->js]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.form :as form]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.product-selector :as product-selector.q]
            [common.resource.query.product-store :as product-store.q]
            [common.util :refer [spread-obj]]
            [manage.product-selector-page.sections.product-selectors :refer [initial-product-selector-ref]]
            [reitit.frontend.easy :as r.easy]
            [uix.core :refer [defhook] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(def operators
  {"text" [{:label "contains following words" :value "match"}
           {:label "does not contain following words" :value "match-not"}]
   "keyword" [{:label "is exactly" :value "term"}
              {:label "is exactly any of" :value "terms"}
              {:label "is exactly not" :value "term-not"}
              {:label "is exactly none of" :value "terms-not"}]
   "number" [{:label "is" :value "term"}
             {:label "greater than" :value "gt"}
             {:label "greater than or equal" :value "gte"}
             {:label "less than" :value "lt"}
             {:label "less than or equal" :value "lte"}]
   "date-time" [{:label "is" :value "term"}
                {:label "after" :value "gt"}
                {:label "after (including whole day)" :value "gte"}
                {:label "before" :value "lt"}
                {:label "before (including whole day)" :value "lte"}]
   "boolean" [{:label "is" :value "term"}]})

(def sorting-events
  [{:label "Most viewed products"
    :value ["product-viewed" "desc"]}
   {:label "Most basketed products"
    :value ["product-basketed" "desc"]}
   {:label "Most purchased products"
    :value ["product-purchased" "desc"]}
   {:label "Least viewed products"
    :value ["product-viewed" "asc"]}
   {:label "Least basketed products"
    :value ["product-basketed" "asc"]}
   {:label "Least purchased products"
    :value ["product-purchased" "asc"]}])

(def ordering
  [{:label "Ascending"
    :value "asc"}
   {:label "Descending"
    :value "desc"}])

(def granularity-window
  [{:value "day-7" :label "Last 7 days" :window 7 :granularity "day"}
   {:value "day-14" :label "Last 14 days" :window 14 :granularity "day"}
   {:value "day-21" :label "Last 21 days" :window 21 :granularity "day"}
   {:value "day-28" :label "Last 28 days" :window 28 :granularity "day"}
   {:value "hour-24" :label "Last 24 hours" :window 24 :granularity "hour"}])

(def granularity-window-map
  (zipmap (map :value granularity-window)
          (map #(select-keys % [:window :granularity]) granularity-window)))

(defn get-first-filterable-field
  "Retrieve the first filterable field from product-store."
  [fields]
  (first
   (filter #(contains? operators (:type %)) fields)))

(defn get-first-sortable-field
  "Retrieve the first sortable field from product-store."
  [fields]
  (first (filter :sortable fields)))

(defn initial-filter-clause-for-field
  "Return initial filter clause for a `field`."
  [field]
  (let [operator (:value (first (operators (:type field))))
        kind (:kind field)
        value (if (= "boolean" (:type field))
                true
                "")]
    (cond-> {:kind kind
             :operator operator
             :value value
             :label (:label field)}
      (= field kind)
      (assoc :field (:name field))

      (= :meta kind)
      (assoc :meta (:name field)))))

(defn initial-field-sort-clause-for-field
  "Return initial field sort clause for a `field`."
  [field]
  {:kind "field"
   :order "asc"
   :field (:name field)})

(def initial-filter-clause
  (comp initial-filter-clause-for-field get-first-filterable-field))

(def initial-field-sort-clause
  (comp initial-field-sort-clause-for-field get-first-sortable-field))

(def initial-metric-sort-clause
  {:kind "metric"
   :sort-type ["product-viewed" "desc"]
   :granularity-window "day-7"})

(def initial-dynamic-priority-clause
  {:type "user-last-viewed"
   :lookback-days 1
   :max-item-count 1})

(def initial-dynamic-exclude-clause
  {:type "user-last-viewed"
   :lookback-days 1})

(defn ->internal-representation
  [selector]
  (letfn [(f [sort-clause]
            (if (= "metric" (:kind sort-clause))
              (-> sort-clause
                  (assoc :granularity-window (str (:granularity sort-clause) "-"
                                                  (:window sort-clause)))
                  (assoc :sort-type ((juxt :metric :order) sort-clause))
                  (dissoc :granularity :window :order :metric))
              sort-clause))
          (meta->field [{:keys [kind label name] :as field}]
            (cond-> field
              (= "meta" kind) (set/rename-keys {:meta :field})
              (empty? label) (assoc :label (str name (when (= "meta" kind) " (default)")))))]
    (if (:composite-config selector)
      (-> selector
          (assoc :type "combined")
          (update-in [:composite-config :selector-refs]
                     (partial mapv #(merge {:min-limit nil} %))))
      (-> selector
          (assoc :type "single")
          (update-in [:query :sort] (partial mapv f))
          (update-in [:query :required-fields] vec)
          (update-in [:query :filter] (partial mapv meta->field))
          (update :priority (partial into []))
          (update :exclude (partial into []))))))

(defn- clean-selector-ref
  [selector-ref]
  (let [min-limit (:min-limit selector-ref)]
    (if (or (nil? min-limit) (and (number? min-limit) (js/isNaN min-limit)))
      (dissoc selector-ref :min-limit)
      selector-ref)))

(defn ->external-representation
  [selector]
  (letfn [(f [sort-clause]
            (if (= "metric" (:kind sort-clause))
              (-> sort-clause
                  (merge (granularity-window-map (:granularity-window sort-clause)))
                  (dissoc :granularity-window)
                  (assoc :metric (-> sort-clause :sort-type first))
                  (assoc :order (-> sort-clause :sort-type second))
                  (dissoc :sort-type))
              sort-clause))
          (field->meta [{:keys [kind] :as field}]
            (if (= "meta" kind)
              (set/rename-keys field {:field :meta})
              field))]
    (case (:type selector)
      "combined" (-> (select-keys selector [:name :composite-config])
                     (update-in [:composite-config :selector-refs]
                                (partial mapv clean-selector-ref)))
      (-> (select-keys selector [:name :query :match :priority :exclude])
          (update-in [:query :sort] (partial mapv f))
          (update-in [:query :filter] (partial mapv field->meta))))))

(def field
  [:or {:error/message "Please select a product field."}
   [:string {:min 1 :error/message "Please select a product field."}]
   [:int]])

(def operator
  [:enum {:error/fn (fn [{:keys [value]} _]
                      (if (str/blank? value)
                        "Please select an operator."
                        "Invalid operator selected."))}
   "term" "terms" "term-not" "terms-not" "lt" "gt" "lte" "gte" "match" "match-not"])

(def value
  [:or {:error/message "The field should not be empty."}
   [:string {:min 1 :error/message "The field should not be empty."}]
   [:boolean]
   [:+ :string]
   number?])

(def filter-clause
  [:map
   [:field field]
   [:operator operator]
   [:value value]])

(def schema
  [:multi {:dispatch :type}
   ["single" [:map
              [:name
               [:string {:min 1 :max 70
                         :error/fn (fn [{:keys [value]} _]
                                     (if (str/blank? value)
                                       "The field should not be empty."
                                       "Name can not be longer than 70 characters."))}]]
              [:query
               [:map
                [:filter [:* filter-clause]]]]]]
   ["combined" [:map
                [:name
                 [:string {:min 1 :max 70
                           :error/fn (fn [{:keys [value]} _]
                                       (if (str/blank? value)
                                         "The field should not be empty."
                                         "Name can not be longer than 70 characters."))}]]
                [:composite-config
                 [:map
                  [:selector-refs
                   [:vector {:min 2
                             :error/message "Please add at least 2 product selectors."}
                    [:and
                     [:map
                      [:selector-ref-id [:string {:min 1
                                                  :error/message "Please select a product selector."}]]
                      [:selector-limit [:int {:min 1
                                              :error/fn (fn [{:keys [value]} _]
                                                          (if (str/blank? value)
                                                            "The field should not be empty."
                                                            "Should be at least 1."))}]]
                      [:min-limit {:optional true} [:maybe [:int
                                                            {:min 0
                                                             :error/message "Should be at least 0."}]]]]
                     [:fn {:error/message "Min should not be greater than max."
                           :error/path [:min-limit]}
                      (fn [{:keys [min-limit selector-limit]}]
                        (or (nil? min-limit)
                            (<= min-limit selector-limit)))]]]]]]]]])

(def default-values
  {:name ""
   :type "single"

   :query {:version 1 :filter [] :sort [] :required-fields []}
   :match []
   :priority []
   :exclude []

   :composite-config {:selector-refs [initial-product-selector-ref initial-product-selector-ref]
                      :combination-strategy "union"}})

(defhook use-product-selector-form [product-selector]
  (let [{:keys [advertiser-id] :as url-context} (use-subscribe [:url-context])
        [mode set-mode] (uix/use-state (if product-selector :read :create))

        initial-value (->js (or (some-> product-selector ->internal-representation)
                                default-values))
        {:keys [handleSubmit reset] :as form-control}
        (form/use-form #js {:mode "onSubmit"
                            :values initial-value
                            :resolver (form/use-malli-resolver schema)})

        {create-product-selector :mutate} (use-mutation (product-selector.q/create-product-selector-mutation
                                                         {:advertiser-id advertiser-id}))
        {patch-product-selector :mutate} (use-mutation (product-selector.q/patch-product-selector-mutation
                                                        {:advertiser-id advertiser-id
                                                         :product-selector-id (:id product-selector)}))

        on-submit (uix/use-callback
                   (fn [values]
                     (let [payload (-> values ->clj ->external-representation)
                           product-selector (dissoc product-selector :valid :updated-at :advertiser-id :id)]
                       (cond
                         (nil? product-selector)
                         (create-product-selector payload
                                                  #js {:onSuccess (fn [resp]
                                                                    (r.easy/navigate
                                                                     :advertiser/product-selector
                                                                     {:path-params url-context
                                                                      :query-params {:product-selector-id (.-id resp)}}))})

                         (not= payload product-selector)
                         (patch-product-selector payload
                                                 #js {:onSuccess #(set-mode :read)})

                         :else (set-mode :read))))
                   [product-selector create-product-selector patch-product-selector url-context])

        on-edit-cancel (uix/use-callback
                        (fn []
                          (reset initial-value)
                          (set-mode :read))
                        [reset initial-value])]
    {:form-control form-control
     :on-submit (handleSubmit on-submit)
     :on-edit-cancel on-edit-cancel
     :mode mode
     :set-mode set-mode}))

(defn- get-product-store-fields [^js data]
  (->> data
       ->clj
       :fields
       (map #(assoc % :kind "field" :label (:name %)))
       (into [{:kind "meta" :type "keyword" :name "sku" :label "sku (default)"}
              {:kind "meta" :type "date-time" :name "updated-at" :label "updated at (default)"}])))

(defhook use-product-store-fields []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {product-store-fields :data} (use-query (spread-obj (product-store.q/product-store-query
                                                             {:advertiser-id advertiser-id})
                                                            #js {:select get-product-store-fields}))]
    product-store-fields))

(defhook use-field-options []
  (let [product-store-fields (use-product-store-fields)]
    (uix/use-memo
     (fn []
       (map #(assoc %
                    :value (:name %)
                    :field (:name %)
                    :label (or (:label %) (:name %)))
            product-store-fields))
     [product-store-fields])))
